import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Paper,
  Stack,
  Grid,
  Typography,
  TextField,
  Tooltip,
  Checkbox,
  Divider
} from '@mui/material';

// --- HELPER COMPONENT FOR ROWS ---
const ModalFormRow = ({ label, children, required }) => (
  <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
    <Grid item xs={12} md={4}>
      <Typography variant="subtitle2" sx={{ mt: 1, display: 'block' }}>
        {label}
        {required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
      </Typography>
    </Grid>
    <Grid item xs={12} md={8}>
      {children}
    </Grid>
  </Grid>
);

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: 3, bgcolor: '#fdfdfd', minHeight: '550px' }}>
            {children}
        </Box>
      )}
    </div>
  );
}

export default function ExistingRightsDialog({ open, onClose, onSave, initialData }) {
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState(initialData || {});

  useEffect(() => {
    if (open) setFormData(initialData || {});
  }, [open, initialData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Existing Rights (Sect 42(2))
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, bgcolor: '#fdfdfd' }}>
        <Paper square elevation={0} sx={{ borderBottom: '1px solid #e0e0e0', bgcolor: '#f0f0f0' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            sx={{
              minHeight: 32,
              '& .MuiTab-root': { fontSize: '0.75rem', minHeight: 32, textTransform: 'none', py: 0.5, px: 2, borderRight: '1px solid #e0e0e0' },
              '& .Mui-selected': { bgcolor: '#fff', fontWeight: 'bold' }
            }}
          >
            <Tab label="1. Section 42(2) End." />
            <Tab label="2. Section 42(2) End." />
            <Tab label="3. Existing Rights (e.g. usufruct; fiduciary etc)/Estate Duty" />
          </Tabs>
        </Paper>

        {/* ================= TAB 1: SECTION 42(2) END ================= */}
        <TabPanel value={tabValue} index={0}>
            <Stack spacing={0.5}>
                <ModalFormRow label="Conveyancer's Cert. for description discrepancies?" required>
                    <TextField name="conveyancerCert" value={formData.conveyancerCert || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fffde7', '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Confirmation that buyer isn't a Sect. 49(1) person">
                    <TextField name="sect49Confirmation" value={formData.sect49Confirmation || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Power of Attorney to transfer sectional title unit?">
                    <TextField name="powerOfAttorney" value={formData.powerOfAttorney || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Were there any prohibitions to the sale?">
                    <TextField name="prohibitions" value={formData.prohibitions || ''} onChange={handleChange} fullWidth multiline rows={3} size="small" variant="outlined" />
                </ModalFormRow>
                <ModalFormRow label="Confirm that interested parties were aware of sale">
                    <TextField name="interestedPartiesAware" value={formData.interestedPartiesAware || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Confirm that sale is legal and binding">
                    <TextField name="saleLegalBinding" value={formData.saleLegalBinding || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Copy of notice to creditors in terms of Sect. 34(2)">
                    <TextField name="noticeToCreditors" value={formData.noticeToCreditors || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Were any objections received ito Sect. 34(3)?">
                    <TextField name="objectionsReceived" value={formData.objectionsReceived || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Was written consent by major heirs/tutors obtained?">
                    <TextField name="writtenConsent" value={formData.writtenConsent || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Valuation by appraiser">
                    <TextField name="appraiserValuation" value={formData.appraiserValuation || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Reasons for manner and conditions of sale">
                    <TextField name="saleReasons" value={formData.saleReasons || ''} onChange={handleChange} fullWidth multiline rows={3} size="small" variant="outlined" />
                </ModalFormRow>
                <ModalFormRow label="Deed of Sale or certified copy attached?">
                    <TextField name="deedOfSaleAttached" value={formData.deedOfSaleAttached || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
            </Stack>
        </TabPanel>

        {/* ================= TAB 2: SECTION 42(2) END (CONT) ================= */}
        <TabPanel value={tabValue} index={1}>
            <Stack spacing={0.5}>
                <ModalFormRow label="Executor's comments on objections to sale">
                    <TextField name="executorComments" value={formData.executorComments || ''} onChange={handleChange} fullWidth multiline rows={4} size="small" variant="outlined" />
                </ModalFormRow>
                <ModalFormRow label="Auctioneer's conditions of sale or certified copy">
                    <TextField name="auctioneerConditions" value={formData.auctioneerConditions || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Auctioneer's assurance - sale was well attended">
                    <TextField name="auctioneerAssurance" value={formData.auctioneerAssurance || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="Names of newspapers in which sale was advertised and cuttings of adverts">
                    <TextField name="newspapersAdvertised" value={formData.newspapersAdvertised || ''} onChange={handleChange} fullWidth multiline rows={4} size="small" variant="outlined" />
                </ModalFormRow>
                <ModalFormRow label="Are there mineral rights registered over this property?">
                    <Checkbox name="mineralRights" checked={formData.mineralRights || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>
                <ModalFormRow label="If so, for which minerals?">
                    <TextField name="minerals" value={formData.minerals || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="In which magisterial district is this property situated?">
                    <TextField name="magisterialDistrict" value={formData.magisterialDistrict || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
                <ModalFormRow label="What % share does Deceased have in these rights?">
                    <TextField name="deceasedShareRights" value={formData.deceasedShareRights || '0.00'} onChange={handleChange} sx={{ width: '100px', '& .MuiInputBase-input': { textAlign: 'right' } }} size="small" />
                </ModalFormRow>
                <Box sx={{ mt: 4 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 2, display: 'block' }}>If any prospecting took place:</Typography>
                    <ModalFormRow label="State company name, period and which minerals">
                        <TextField name="prospectingDetails" value={formData.prospectingDetails || ''} onChange={handleChange} fullWidth multiline rows={4} size="small" variant="outlined" />
                    </ModalFormRow>
                </Box>
            </Stack>
        </TabPanel>

        {/* ================= TAB 3: EXISTING RIGHTS / ESTATE DUTY ================= */}
        <TabPanel value={tabValue} index={2}>
            <Stack spacing={0.5}>
                <ModalFormRow label="Did the Deceased hold a usufruct over this property?">
                    <Checkbox name="heldUsufruct" checked={formData.heldUsufruct || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>
                <ModalFormRow label="Date on which created?">
                    <TextField name="usufructCreatedDate" value={formData.usufructCreatedDate || ''} onChange={handleChange} sx={{ width: '150px' }} size="small" />
                </ModalFormRow>
                <ModalFormRow label="Original asset value as declared in previous estate">
                    <TextField name="originalAssetValue" value={formData.originalAssetValue || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} size="small" />
                </ModalFormRow>
                <ModalFormRow label="Yield for calculation of s5 value">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <TextField name="yield" value={formData.yield || '12.000000'} onChange={handleChange} sx={{ width: '120px' }} size="small" />
                        <Typography variant="caption">%</Typography>
                    </Stack>
                </ModalFormRow>
                <ModalFormRow label="Select ultimate beneficiaries of this property">
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField name="ultimateBeneficiaries" value={formData.ultimateBeneficiaries || ''} onChange={handleChange} fullWidth multiline rows={3} size="small" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000' }}>Heirs</Button>
                    </Stack>
                </ModalFormRow>
                <ModalFormRow label="Value of right ito Sect. 5 of the Estate Duty Act">
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField name="sect5Value" value={formData.sect5Value || '0.00'} onChange={handleChange} sx={{ width: '150px' }} size="small" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000' }}>Table A</Button>
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000' }}>Table B</Button>
                    </Stack>
                </ModalFormRow>
                <ModalFormRow label="Describe origin of this usufructuary/fiduciary right">
                    <TextField name="rightOrigin" value={formData.rightOrigin || ''} onChange={handleChange} fullWidth multiline rows={3} size="small" />
                </ModalFormRow>
                <Divider sx={{ my: 2 }} />
                <ModalFormRow label="Did Deceased hold a fiduciary right to the property?">
                    <Checkbox name="heldFiduciaryRight" checked={formData.heldFiduciaryRight || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>
                <ModalFormRow label="Original asset value as declared in previous estate">
                    <TextField name="fiduciaryOriginalValue" value={formData.fiduciaryOriginalValue || '0.00'} onChange={handleChange} sx={{ width: '150px' }} size="small" />
                </ModalFormRow>
                <ModalFormRow label="Yield for calculation of s5 value">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <TextField name="fiduciaryYield" value={formData.fiduciaryYield || '12.000000'} onChange={handleChange} sx={{ width: '120px' }} size="small" />
                        <Typography variant="caption">%</Typography>
                    </Stack>
                </ModalFormRow>
                <ModalFormRow label="Period right to be enjoyed by heir to whom awarded">
                    <TextField name="fiduciaryPeriod" value={formData.fiduciaryPeriod || '0'} onChange={handleChange} sx={{ width: '80px' }} size="small" />
                </ModalFormRow>
                <ModalFormRow label="Consideration, if any, paid by this heir">
                    <TextField name="fiduciaryConsideration" value={formData.fiduciaryConsideration || '0.00'} onChange={handleChange} sx={{ width: '150px' }} size="small" />
                </ModalFormRow>
                <ModalFormRow label="Value of right ito Sect. 5 of the Estate Duty Act">
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField name="fiduciarySect5Value" value={formData.fiduciarySect5Value || '0.00'} onChange={handleChange} sx={{ width: '150px' }} size="small" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000' }}>Table A</Button>
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000' }}>Table B</Button>
                    </Stack>
                </ModalFormRow>
                <ModalFormRow label="Describe any expenses incurred iro this right">
                    <TextField name="expensesDescription" value={formData.expensesDescription || ''} onChange={handleChange} fullWidth size="small" />
                </ModalFormRow>
                <ModalFormRow label="Total value of these incurred expenses (in figures)">
                    <TextField name="expensesValue" value={formData.expensesValue || '0.00'} onChange={handleChange} sx={{ width: '150px' }} size="small" />
                </ModalFormRow>
            </Stack>
        </TabPanel>

      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0', justifyContent: 'center' }}>
         <Stack direction="row" spacing={2}>
             <Button onClick={() => { if(onSave) onSave(formData); onClose(); }} variant="contained" color="success">OK</Button>
             <Button onClick={onClose} variant="contained" color="inherit" sx={{ bgcolor: '#e0e0e0' }}>Cancel</Button>
         </Stack>
      </DialogActions>
    </Dialog>
  );
}